<script setup lang="ts">
import './_index.scss'
import { inject, computed } from 'vue'
import type { InvestmentProject } from '@/server/api/buyer-front/types'
import { investmentProjectKey } from '@/symbols/listing'
import PdAmenitiesList from '@/components/pd/AmenitiesV2/List/index.vue'
import PdAmenitiesCarrousel from '@/components/pd/AmenitiesV2/Carrousel/index.vue'

const { t } = useI18n()

const listing = inject(investmentProjectKey) as InvestmentProject
const categoriesInfo = listing.raw_listing.cms?.categories_info

const getAmenitiesFromCategories = (categoriesInfo: any) => {
  return JSON.parse(categoriesInfo?.['plp-amenities'] || '[]')
}

const amenitiesList = computed(() => {
  return getAmenitiesFromCategories(categoriesInfo)
    .map((amenity: Record<string, any>) => amenity.attributes.title)
    .sort(Intl.Collator().compare)
})

const allAmenitiesHaveAttributes = (): boolean => {
  return getAmenitiesFromCategories(categoriesInfo)
    .every((amenity: Record<string, any>) => hasAllAttributes(amenity.attributes))
}

const displayAmenitiesCarrousel = computed(() => allAmenitiesHaveAttributes())

const hasAllAttributes = (attributes: Record<string, any>): boolean => {
  return !!attributes?.title && !!attributes.image?.data?.attributes?.url
}

const amenitiesData = computed(() => {
  return getAmenitiesFromCategories(categoriesInfo).map((amenity: Record<string, any>, index: any) => ({
    index,
    title: amenity.attributes.title,
    description: amenity.attributes.description,
    imageUrl: amenity.attributes.image.data?.attributes?.url
  }))
})

</script>

<script lang="ts">
export default {
  name: 'PdAmenities'
}
</script>

<template>
  <section
    v-if="amenitiesList.length"
    class="pd-amenities-section mt-72 lg:mt-56 mx-0">
    <div
      v-if="displayAmenitiesCarrousel"
      class="flex w-full lg:w-auto">
      <span class="display-6 lg:display-5 text-pine-600 mx-24 lg:mx-0">
        {{ t('amenities.subTitle') }}
      </span>
    </div>

    <PdAmenitiesCarrousel
      v-if="displayAmenitiesCarrousel"
      class="mt-24 lg:mt-48 md:mt-56"
      :amenities-data="amenitiesData" />

    <PdAmenitiesList
      v-else
      :amenities-list="amenitiesList" />
  </section>
</template>

<i18n src="./i18n.json" lang="json" />
